/**
 * Push Service - سرویس اصلی Push Notifications
 * این سرویس مسئول ارسال نوتیفیکیشن‌ها از Service Worker است
 */

import { getNotificationOptions } from './vapidConfig';
import { subscriptionManager } from './subscriptionManager';

class PushService {
  constructor() {
    this.registration = null;
  }

  /**
   * Initialize push service
   * @returns {Promise<boolean>}
   */
  async initialize() {
    try {
      console.log('🔔 Initializing Push Service...');
      
      if (!('serviceWorker' in navigator)) {
        console.warn('⚠️ Service Worker not supported');
        return false;
      }
      
      this.registration = await navigator.serviceWorker.ready;
      console.log('✅ Push Service initialized');
      
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize Push Service:', error);
      return false;
    }
  }

  /**
   * Show a local notification (without push)
   * این متد برای نوتیفیکیشن‌های محلی است که نیاز به سرور ندارند
   * 
   * @param {string} title - عنوان نوتیفیکیشن
   * @param {Object} options - تنظیمات نوتیفیکیشن
   * @param {string} type - نوع نوتیفیکیشن (welcome, analysis, reminder, etc.)
   * @returns {Promise<boolean>}
   */
  async showLocalNotification(title, options = {}, type = 'general') {
    try {
      console.log(`🔔 Showing local notification: ${title}`);
      
      // Check permission
      if (Notification.permission !== 'granted') {
        console.warn('⚠️ Notification permission not granted');
        return false;
      }
      
      // Initialize if needed
      if (!this.registration) {
        const initialized = await this.initialize();
        if (!initialized) {
          throw new Error('Failed to initialize');
        }
      }
      
      // Get notification options
      const notificationOptions = getNotificationOptions(type, options);
      
      // Show notification via Service Worker
      await this.registration.showNotification(title, notificationOptions);
      
      console.log('✅ Local notification shown successfully');
      return true;
    } catch (error) {
      console.error('❌ Failed to show local notification:', error);
      return false;
    }
  }

  /**
   * Schedule a notification for later
   * برنامه‌ریزی نوتیفیکیشن برای زمان خاص
   * 
   * @param {string} title - عنوان نوتیفیکیشن
   * @param {Object} options - تنظیمات نوتیفیکیشن
   * @param {number} delay - تأخیر به میلی‌ثانیه
   * @param {string} type - نوع نوتیفیکیشن
   * @returns {Promise<number>} - شناسه timeout
   */
  async scheduleNotification(title, options = {}, delay = 0, type = 'general') {
    try {
      console.log(`⏰ Scheduling notification for ${delay}ms: ${title}`);
      
      const timeoutId = setTimeout(async () => {
        await this.showLocalNotification(title, options, type);
      }, delay);
      
      console.log(`✅ Notification scheduled with ID: ${timeoutId}`);
      return timeoutId;
    } catch (error) {
      console.error('❌ Failed to schedule notification:', error);
      return null;
    }
  }

  /**
   * Cancel a scheduled notification
   * لغو نوتیفیکیشن برنامه‌ریزی شده
   * 
   * @param {number} timeoutId - شناسه timeout
   * @returns {boolean}
   */
  cancelScheduledNotification(timeoutId) {
    try {
      if (timeoutId) {
        clearTimeout(timeoutId);
        console.log(`✅ Notification cancelled: ${timeoutId}`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Failed to cancel notification:', error);
      return false;
    }
  }

  /**
   * Request notification permission
   * درخواست مجوز نوتیفیکیشن
   * 
   * @returns {Promise<string>} - 'granted', 'denied', or 'default'
   */
  async requestPermission() {
    return await subscriptionManager.requestPermission();
  }

  /**
   * Subscribe to push notifications
   * اشتراک در نوتیفیکیشن‌های push
   * 
   * @param {string} userId - شناسه کاربر
   * @returns {Promise<boolean>}
   */
  async subscribe(userId = null) {
    const subscription = await subscriptionManager.subscribe(userId);
    return subscription !== null;
  }

  /**
   * Unsubscribe from push notifications
   * لغو اشتراک از نوتیفیکیشن‌های push
   * 
   * @param {string} userId - شناسه کاربر
   * @returns {Promise<boolean>}
   */
  async unsubscribe(userId = null) {
    return await subscriptionManager.unsubscribe(userId);
  }

  /**
   * Check if subscribed to push notifications
   * بررسی اشتراک در نوتیفیکیشن‌های push
   * 
   * @returns {Promise<boolean>}
   */
  async isSubscribed() {
    return await subscriptionManager.isSubscribed();
  }

  /**
   * Get current subscription
   * دریافت اشتراک فعلی
   * 
   * @returns {Promise<Object|null>}
   */
  async getSubscription() {
    return await subscriptionManager.getSubscription();
  }

  /**
   * Test notification
   * تست نوتیفیکیشن برای بررسی عملکرد
   * 
   * @returns {Promise<boolean>}
   */
  async testNotification() {
    try {
      console.log('🧪 Testing notification...');
      
      const result = await this.showLocalNotification(
        '🎉 تست نوتیفیکیشن',
        {
          body: 'این یک نوتیفیکیشن تستی است. اگر این پیام را می‌بینید، نوتیفیکیشن‌ها به درستی کار می‌کنند!',
          data: { url: '/', test: true },
          actions: [
            { action: 'open', title: 'باز کردن', icon: '/logo_pwa.png' },
            { action: 'dismiss', title: 'بستن' }
          ],
        },
        'general'
      );
      
      if (result) {
        console.log('✅ Test notification sent successfully');
      } else {
        console.log('❌ Test notification failed');
      }
      
      return result;
    } catch (error) {
      console.error('❌ Test notification error:', error);
      return false;
    }
  }
}

// Export singleton instance
export const pushService = new PushService();

export default pushService;



